import React, {useState, useEffect} from "react";
import {Modal, Form} from "react-bootstrap";
import {
    ENTITY_ID_WATER_WASH,
    ENTITY_ID_ADVISORS,
    ENTITY_ID_BRANCH,
    ENTITY_ID_DEALER,
    ENTITY_ID_VEHICLE_MODEL
} from "../generic/Constants";
import {genericGetAll, apiGenericCreate, genericGetOne, genericSelectGetAll} from "../generic/crud";
import {showToast, TOAST_FAILURE, TOAST_SUCCESS} from "../../utils/toast/Toast";
import { convertDateToRelativeTime } from "../../utils/Utils";
import {AGCSelect} from "../../generic-components/AGCSelect";
import {AGCStaticSelect} from "../../generic-components/AGCStaticSelect";
import Select from 'react-select';
import {useThemeMode, systemMode} from '../../../_metronic/partials/layout/theme-mode/ThemeModeProvider';

const JOB_TYPES = [
    {id: 'FREE_SERVICE', name: 'Free Service'},
    {id: 'PAID_SERVICE', name: 'Paid Service'},
    {id: 'RUNNING_REPAIR', name: 'Running Repair'},
    {id: 'ACCIDENTAL_REPAIR', name: 'Accidental Repair'}
];

export function WaterWashWizardDialog(props) {
    const {show, handleClose, branchId, branchName, onSave} = props;
    const {mode} = useThemeMode();
    const isDarkMode = mode === 'dark' || (mode === 'system' && systemMode === 'dark');
    
    const [currentStep, setCurrentStep] = useState(1);
    const [vehicleNumber, setVehicleNumber] = useState("");
    const [waterWashRecords, setWaterWashRecords] = useState([]);
    const [loading, setLoading] = useState(false);
    
    // Step 3 State
    const [washDate, setWashDate] = useState(new Date().toISOString().slice(0, 16));
    const [vehicleModelId, setVehicleModelId] = useState(null);
    const [jobType, setJobType] = useState(JOB_TYPES.find(jt => jt.id === 'PAID_SERVICE'));
    const [advisorId, setAdvisorId] = useState(null);
    const [isRepeatWash, setIsRepeatWash] = useState(false);
    
    const [dealerBrand, setDealerBrand] = useState(null);
    const [branchAdvisorIds, setBranchAdvisorIds] = useState([]);
    const [allAdvisors, setAllAdvisors] = useState([]);
    const [filteredAdvisors, setFilteredAdvisors] = useState([]);

    // Reset state when dialog opens
    useEffect(() => {
        if (show) {
            setCurrentStep(1);
            setVehicleNumber("");
            setWaterWashRecords([]);
            setLoading(false);
            setWashDate(new Date().toISOString().slice(0, 16));
            setVehicleModelId(null);
            setJobType(JOB_TYPES.find(jt => jt.id === 'PAID_SERVICE'));
            setAdvisorId(null);
            setIsRepeatWash(false);
        }
    }, [show]);

    // Fetch all advisors
    useEffect(() => {
        if (show) {
            genericSelectGetAll(ENTITY_ID_ADVISORS).then(({data}) => {
                const safeData = Array.isArray(data) ? data : [];
                setAllAdvisors(safeData);
            }).catch(() => {
                setAllAdvisors([]);
            });
        }
    }, [show]);

    // Filter advisors based on branch's advisorIds
    useEffect(() => {
        if (branchAdvisorIds.length > 0 && allAdvisors.length > 0) {
            const filtered = allAdvisors.filter(advisor =>
                branchAdvisorIds.includes(advisor.id)
            );
            setFilteredAdvisors(filtered);
        } else {
            setFilteredAdvisors([]);
        }
    }, [branchAdvisorIds, allAdvisors]);

    // Fetch branch details
    useEffect(() => {
        if (show && branchId) {
            genericGetOne(ENTITY_ID_BRANCH, branchId).then(({data: branchData}) => {
                if (branchData) {
                    if (branchData.advisorIds && Array.isArray(branchData.advisorIds)) {
                        setBranchAdvisorIds(branchData.advisorIds);
                    } else {
                        setBranchAdvisorIds([]);
                    }

                    if (branchData.dealerId) {
                        genericGetOne(ENTITY_ID_DEALER, branchData.dealerId).then(({data: dealerData}) => {
                            if (dealerData && dealerData.brand) {
                                setDealerBrand(dealerData.brand);
                            }
                        }).catch(() => {
                            setDealerBrand(null);
                        });
                    }
                }
            }).catch(() => {
                setDealerBrand(null);
                setBranchAdvisorIds([]);
            });
        }
    }, [show, branchId]);

    const handleNext = async () => {
        if (!vehicleNumber || vehicleNumber.trim() === "") {
            showToast("Please enter a vehicle number", TOAST_FAILURE);
            return;
        }

        if (!branchId) {
            showToast("Please select a branch first", TOAST_FAILURE);
            return;
        }

        setLoading(true);
        try {
            const response = await genericGetAll(ENTITY_ID_WATER_WASH, {
                page: 1,
                sizePerPage: 1000,
                currentIndex: 0,
                searchField: 'vehicleNumber',
                searchString: vehicleNumber.trim().toUpperCase()
            });

            if (response.data && response.data.entities) {
                // Sort by createdOn, latest first
                const sortedRecords = response.data.entities.sort((a, b) => {
                    return new Date(b.createdOn) - new Date(a.createdOn);
                });
                setWaterWashRecords(sortedRecords);
            } else {
                setWaterWashRecords([]);
            }
            setCurrentStep(2);
        } catch (error) {
            console.error("Error fetching water wash records:", error);
            showToast("Error fetching water wash records", TOAST_FAILURE);
            setWaterWashRecords([]);
        } finally {
            setLoading(false);
        }
    };

    const handleBack = () => {
        if (currentStep === 3) {
            setCurrentStep(2);
        } else {
            setCurrentStep(1);
        }
    };

    const handleCreateNew = () => {
        setWashDate(new Date().toISOString().slice(0, 16));
        setCurrentStep(3);
    };

    const submitNewWash = async () => {
        if (!washDate) {
            showToast("Wash Date is required", TOAST_FAILURE);
            return;
        }
        if (!vehicleModelId) {
            showToast("Vehicle Model is required", TOAST_FAILURE);
            return;
        }
        if (!jobType) {
            showToast("Job Type is required", TOAST_FAILURE);
            return;
        }
        if (!advisorId) {
            showToast("Service Advisor is required", TOAST_FAILURE);
            return;
        }

        setLoading(true);
        try {
            const submissionData = {
                vehicleNumber: vehicleNumber,
                branchId: branchId,
                branchName: branchName,
                vehicleModelId: vehicleModelId?.id || vehicleModelId,
                jobType: jobType?.id || jobType,
                advisorId: advisorId?.id || advisorId,
                isRepeatWash: isRepeatWash,
                createdOn: new Date(washDate)
            };

            await apiGenericCreate(ENTITY_ID_WATER_WASH, submissionData, { suppressToast: true });
            
            showToast("Water Wash record created successfully!", TOAST_SUCCESS);
            if (onSave) onSave();
            handleDialogClose(); 
        } catch (error) {
             let errorMessage = error?.response?.data?.message || error?.response?.data || error?.message || "An error occurred";
            if (typeof errorMessage === 'object') {
                try {
                    errorMessage = JSON.stringify(errorMessage);
                } catch (e) {
                    errorMessage = "An unknown error occurred";
                }
            }
            showToast("Error: " + errorMessage, TOAST_FAILURE);
            setLoading(false);
        }
    };

    const handleDialogClose = () => {
        setCurrentStep(1);
        setVehicleNumber("");
        setWaterWashRecords([]);
        handleClose();
    };

    const formatDate = (dateString) => {
        if (!dateString) return "N/A";
        const date = new Date(dateString);
        return date.toLocaleDateString('en-IN', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    // Custom styles for dark theme
    const customSelectStyles = isDarkMode ? {
        control: (provided, state) => ({
            ...provided,
            backgroundColor: '#1e1e2d',
            borderColor: state.isFocused ? '#009ef7' : '#2b2b40',
            color: '#a1a5b7',
            '&:hover': {
                borderColor: '#009ef7'
            }
        }),
        menu: (provided) => ({
            ...provided,
            backgroundColor: '#1e1e2d',
            border: '1px solid #2b2b40'
        }),
        option: (provided, state) => ({
            ...provided,
            backgroundColor: state.isFocused ? '#2b2b40' : '#1e1e2d',
            color: '#a1a5b7',
            '&:hover': {
                backgroundColor: '#2b2b40'
            }
        }),
        singleValue: (provided) => ({
            ...provided,
            color: '#a1a5b7'
        }),
        input: (provided) => ({
            ...provided,
            color: '#a1a5b7'
        }),
        placeholder: (provided) => ({
            ...provided,
            color: '#6c7293'
        })
    } : {};

    return (
        <>
            <Modal show={show} onHide={handleDialogClose} size="lg" centered>
                <Modal.Header closeButton>
                    <Modal.Title>
                        {currentStep === 1 ? 'Water Wash - Enter Vehicle Details' : 
                         currentStep === 2 ? `Water Wash History - ${vehicleNumber}` :
                         `New Water Wash - ${vehicleNumber}`}
                    </Modal.Title>
                </Modal.Header>

                <Modal.Body style={{minHeight: '300px'}}>
                    {/* Step 1: Vehicle Number Input */}
                    {currentStep === 1 && (
                        <div className="row">
                            <div className="col-12 mb-4">
                                <label className="form-label fw-bold">Branch</label>
                                <input
                                    type="text"
                                    className="form-control"
                                    value={branchName || ""}
                                    disabled={true}
                                    readOnly
                                />
                            </div>

                            <div className="col-12 mb-4">
                                <label className="form-label fw-bold required">Vehicle Number</label>
                                <input
                                    type="text"
                                    className="form-control form-control-lg"
                                    placeholder="e.g., KA01AB1234"
                                    value={vehicleNumber}
                                    onChange={(e) => setVehicleNumber(e.target.value.toUpperCase())}
                                    onKeyPress={(e) => {
                                        if (e.key === 'Enter') {
                                            handleNext();
                                        }
                                    }}
                                    disabled={loading}
                                    autoFocus
                                />
                            </div>
                        </div>
                    )}

                    {/* Step 2: Water Wash History */}
                    {currentStep === 2 && (
                        <div>
                            <div className="d-flex justify-content-between align-items-center mb-4">
                                <div>
                                    <h5 className="mb-1">Previous Water Wash Records</h5>
                                    <p className="text-muted mb-0">
                                        <span className="badge badge-light-primary">{waterWashRecords.length} records found</span>
                                    </p>
                                </div>
                            </div>

                            {waterWashRecords.length === 0 ? (
                                <div className="text-center py-5">
                                    <i className="bi bi-droplet-half text-muted" style={{fontSize: '3rem'}}></i>
                                    <p className="text-muted mt-3">No water wash records found for this vehicle.</p>
                                    <p className="text-muted">This will be the first water wash record.</p>
                                </div>
                            ) : (
                                <div className="table-responsive" style={{maxHeight: '400px', overflowY: 'auto'}}>
                                    <table className="table table-row-bordered table-row-gray-100 align-middle gs-0 gy-3">
                                        <thead>
                                            <tr className="fw-bold text-muted text-uppercase fs-7">
                                                <th className="min-w-50px">#</th>
                                                <th className="min-w-150px text-start">Wash Date</th>
                                                <th className="min-w-150px">Branch</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {waterWashRecords.map((record, index) => (
                                                <tr key={record.id}>
                                                    <td>
                                                        <span className="text-muted fw-semibold d-block fs-7">{index + 1}</span>
                                                    </td>
                                                    <td className="text-start">
                                                        <div className="d-flex flex-column justify-content-center">
                                                            <span className="text-dark fw-bold text-hover-primary mb-1 fs-6">
                                                                {convertDateToRelativeTime(record.createdOn)}
                                                            </span>
                                                            <span className="text-muted fw-semibold d-block fs-7">
                                                                {formatDate(record.createdOn)}
                                                            </span>
                                                        </div>
                                                    </td>
                                                    <td>
                                                        <span className="text-dark fw-bold d-block fs-6">
                                                            {record.branchName || "N/A"}
                                                        </span>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Step 3: Create New Water Wash */}
                    {currentStep === 3 && (
                        <div className="row">
                            <div className="col-12 mb-4">
                                <label className="form-label fw-bold">Branch</label>
                                <input
                                    type="text"
                                    className="form-control"
                                    value={branchName || ""}
                                    disabled={true}
                                    readOnly
                                />
                            </div>

                            <div className="col-md-6 mb-4">
                                <label className="form-label fw-bold">Vehicle Number</label>
                                <input
                                    type="text"
                                    className="form-control"
                                    value={vehicleNumber}
                                    disabled={true}
                                    readOnly
                                />
                            </div>

                            <div className="col-md-6 mb-4">
                                <label className="form-label required">Wash Date</label>
                                <input
                                    type="datetime-local"
                                    className="form-control"
                                    value={washDate}
                                    onChange={(e) => setWashDate(e.target.value)}
                                />
                            </div>

                            <div className="col-md-6 mb-4">
                                <AGCSelect
                                    name="Vehicle Model"
                                    entityId={ENTITY_ID_VEHICLE_MODEL}
                                    value={vehicleModelId}
                                    onChange={val => setVehicleModelId(val)}
                                    toolTip="Select Vehicle Model"
                                    filterField={dealerBrand ? "oem" : null}
                                    filterValue={dealerBrand}
                                    required={true}
                                />
                            </div>

                            <div className="col-md-6 mb-4">
                                <AGCStaticSelect
                                    data={JOB_TYPES}
                                    name="Job Type"
                                    value={jobType}
                                    onChange={val => setJobType(val)}
                                    toolTip="Select Job Type"
                                    includeAllOption={false}
                                    required={true}
                                />
                            </div>

                            <div className="col-md-6 mb-4">
                                <label className="form-label required fw-bold">Service Advisor</label>
                                <Select
                                    options={filteredAdvisors}
                                    value={advisorId}
                                    getOptionLabel={(option) => option.name}
                                    getOptionValue={(option) => option.id}
                                    onChange={option => setAdvisorId(option)}
                                    styles={customSelectStyles}
                                    placeholder="Select Advisor"
                                    isClearable
                                    isDisabled={filteredAdvisors.length === 0}
                                />
                                {filteredAdvisors.length === 0 && (
                                    <div className="text-muted fs-8 mt-1">No advisors assigned to this branch</div>
                                )}
                            </div>

                            <div className="col-md-6 mb-4 d-flex align-items-center">
                                <Form.Check
                                    type="checkbox"
                                    id="isRepeatWash"
                                    label="Is Repeat Wash"
                                    checked={isRepeatWash}
                                    onChange={(e) => setIsRepeatWash(e.target.checked)}
                                    className="mt-4"
                                />
                            </div>
                        </div>
                    )}
                </Modal.Body>

                <Modal.Footer>
                    {currentStep === 1 && (
                        <>
                            <button
                                type="button"
                                className="btn btn-light"
                                onClick={handleDialogClose}
                                disabled={loading}
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                className="btn btn-primary"
                                onClick={handleNext}
                                disabled={loading}
                            >
                                {loading ? (
                                    <>
                                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                        Loading...
                                    </>
                                ) : (
                                    <>
                                        Next
                                        <i className="bi bi-arrow-right ms-2"></i>
                                    </>
                                )}
                            </button>
                        </>
                    )}

                    {currentStep === 2 && (
                        <>
                            <button
                                type="button"
                                className="btn btn-light"
                                onClick={handleBack}
                            >
                                <i className="bi bi-arrow-left me-2"></i>
                                Back
                            </button>
                            <button
                                type="button"
                                className="btn btn-secondary"
                                onClick={handleDialogClose}
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                className="btn btn-success"
                                onClick={handleCreateNew}
                            >
                                <i className="bi bi-plus-circle me-2"></i>
                                Add
                            </button>
                        </>
                    )}

                    {currentStep === 3 && (
                        <>
                            <button
                                type="button"
                                className="btn btn-light"
                                onClick={handleBack}
                                disabled={loading}
                            >
                                <i className="bi bi-arrow-left me-2"></i>
                                Back
                            </button>
                            <button
                                type="button"
                                className="btn btn-secondary"
                                onClick={handleDialogClose}
                                disabled={loading}
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                className="btn btn-primary"
                                onClick={submitNewWash}
                                disabled={loading}
                            >
                                {loading ? (
                                    <>
                                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                        Saving...
                                    </>
                                ) : (
                                    'Save'
                                )}
                            </button>
                        </>
                    )}
                </Modal.Footer>
            </Modal>
        </>
    );
}

